import { useNotifications } from '@models/notification';
import { timeAgo } from '@services/time.service';
import styled from 'styled-components';
import PliableLoader from './loaders/PliableLoader.component';
import PipelineNodeName from './pipelineNodes/PipelineNodeName.component';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SuccessAlert from './statusIndicators/SuccessAlert.component';
import { useNodesThatNeedBuilding } from '@models/pipelineNode';
import { usePipelineNodeRelationships, usePipelineNodes } from '@stores/data.store';
import { useMeasures } from '@models/reportBuilder';
import MeasureName from './reportBuilder/MeasureName.component';
import { getPromptAnswer, runBuild } from '@services/alert/alert.service';
import toast from '@services/toast.service';
import { getErrorMessage } from '@services/errors.service';
import { Dropdown as BSDropdown } from 'react-bootstrap';
import { DraftOnly, ProdOnly } from './project/DraftModeRequired.component';
import { useHotkeys } from 'react-hotkeys-hook';
import { subscribe, unsubscribe, emit } from '@nextcloud/event-bus';
import { validateMetric } from "@services/metricValidation.service";
import { NODE_TYPE_GROUPS } from '@services/modeling.service';

interface MessageProps {
    title: string;
    type: string;
    time: Date;
    description: string;
    objectType?: string;
    objectId?: string;
}

const MessageStyles = styled.div`
font-family: "Poppins";
color: black;
padding: 1rem;
overflow: hidden;
border-bottom: solid 1px var(--ct-border-color);

&:last-child {
    border-bottom: none;
}

&:hover {
    cursor: pointer;
    background-color: #FF9F001A;

}
.header {
    display: flex;
    align-items: center;

    .icon {
        width: 40px;
        font-size: 28px;
    }

    .title {
        line-height: 1rem;
        font-weight: bold;
    }

    .time {
        font-size: 13px;
        color: var(--ct-muted-text);
    }

    margin-bottom: 10px;
}

.description {
    color: var(--ct-body-color);
    font-size: 13px;
}

&:first-child {
    border-top-right-radius: 5px;
    border-top-left-radius: 5px;
}

&:last-child {
    border-bottom-right-radius: 5px;
    border-bottom-left-radius: 5px;
}

&.warning {
    div.icon {
        color: rgb(238, 205, 76);
    }
}

&.BUILD_ERROR {
    div.icon {
        color: var(--ct-danger);
    }
}

&.BUILD_NEEDED {
    div.icon {
        color: var(--pliable-yellow);
    }
}

&.MISSING_RELATIONSHIPS {
    div.icon {
        color: var(--pliable-purple);
    }
}

&.MISSING_MAPPINGS {
    div.icon {
        color: var(--ct-danger);
    }
}

&.MISSING_JOIN_TREE, &.MISSING_SOURCE_COLUMN, &.MISSING_ROLLUP_TYPE, &.MISSING_FORMULA, &.MISSING_METRIC_SOURCE {
    div.icon {
        color: var(--ct-danger);
    }
}

&.NO_BUSINESS_OBJECTS {
    div.icon {
        color: var(--pliable-yellow);
    }
}

&.info {
    div.icon {
        color: var(--ct-info);
    }
}

`

/**
 * Message types:
 * 
 * - Generic "X nodes need building"
 * - Build error - this particular node failed for this reason
 * - Generic "this node has no sources"
 * - Generic "this relationship has no lookup parameters"
 * 
 */

const Message = (props: MessageProps) => {
    
    return <MessageStyles className={'message ' + props.type}>
        <div className="header">
            <div className="icon">
                {props.type == "BUILD_ERROR" && <i className="mdi mdi-close-circle"></i>}
                {props.type == "BUILD_NEEDED" && <i className="mdi mdi-hammer-screwdriver"></i>}
                {props.type == 'MISSING_RELATIONSHIPS' && <i className="mdi mdi-link-off"></i>}
                {props.type == 'MISSING_MAPPINGS' && <i className="mdi mdi-database-alert"></i>}
                {props.type == 'MISSING_JOIN_TREE' && <i className="mdi mdi-link-off"></i>}
                {props.type == 'MISSING_METRIC_SOURCE' && <i className="mdi mdi-database-alert"></i>}
                {props.type == 'MISSING_SOURCE_COLUMN' && <i className="mdi mdi-database-alert"></i>}
                {props.type == 'MISSING_METRIC_FORMULA' && <i className="mdi mdi-function-variant"></i>}
                {props.type == 'NO_BUSINESS_OBJECTS' && <i className="mdi mdi-star-off"></i>}
                {props.type == 'MISSING_DIMENSION_CONNECTIONS' && <i className="mdi mdi-link-off"></i>}
            </div>
            <div>
                <div className="time">
                    {timeAgo(props.time)}
                </div>
                <div className="title">
                {props.title}
                </div>
                
            </div>
            

        </div>
        <div className="description">
            {props.type == 'BUILD_ERROR' && <>
                <strong><PipelineNodeName pipelineNodeId={props.objectId}/></strong> failed to build.
            </>}
            {props.type == 'BUILD_NEEDED' && <>
                Click to run a build.
            </>}
            {props.type == 'MISSING_RELATIONSHIPS' && <>
                <strong><PipelineNodeName pipelineNodeId={props.objectId}/></strong> has no relationships to other entities. Click here to connect it to your data model.
            </>}
            {props.type == 'MISSING_MAPPINGS' && <>
                <strong><PipelineNodeName pipelineNodeId={props.objectId}/></strong> has no source data. Click here to connect a data source.
            </>}
            {props.type == 'MISSING_JOIN_TREE' && <>
                Metric <strong><MeasureName measureId={props.objectId as string}/></strong> is not connected to all of your dimensions. Click here to resolve.
            </>}
            {props.type == 'MISSING_METRIC_FORMULA' && <>
                Metric <strong><MeasureName measureId={props.objectId as string}/></strong> does not have a valid formula. Click here to resolve.
            </>}
            {props.type == 'NO_BUSINESS_OBJECTS' && <>
                You haven't created any entities yet. Click here to get started.
            </>}
            {props.type == 'MISSING_METRIC_SOURCE' && <>
                Metric <strong><MeasureName measureId={props.objectId as string}/></strong> is not connected to your data model. Click here to resolve.

            </>}
            {props.type == 'MISSING_SOURCE_COLUMN' && <>
                Metric <strong><MeasureName measureId={props.objectId as string}/></strong> does not have a source column selected. Click here to resolve.

            </>}
            {props.type == 'MISSING_DIMENSION_CONNECTIONS' && <>
                Metric <strong><MeasureName measureId={props.objectId as string}/></strong> is not connected to all of your dimensions. Click here to resolve.
            </>}


        </div>
    </MessageStyles>
}

const Indicator = styled.div`
position: absolute;
top: 32px;
left: 32px;
background-color: var(--ct-danger);
width: 10px;
height: 10px;
border-radius: 5px;
`

interface Props {
    onRunBuild?: () => void;
    notifications: MessageProps[];
    nodesNeedBuilding?: number;
}

export const NotificationsList = (props: Props) => {
    
    const navigate = useNavigate();

    const onClickBuild = useCallback(() => {
        props.onRunBuild && props.onRunBuild();
    }, [props.onRunBuild])

    const onClickMessage = useCallback((msg: MessageProps) => {
        switch (msg.type) {
            case 'BUILD_ERROR':
                navigate(`/node/${msg.objectId}/config`);
                break;
            case 'MISSING_RELATIONSHIPS':
                navigate(`/model/visual?focusOn=PipelineNode:${msg.objectId}`);
                break;
            case 'MISSING_MAPPINGS':
                navigate(`/wizard/data-source?next=cleaning&bo_context=${msg.objectId}`);
                break;
            case 'MISSING_JOIN_TREE':
            case 'MISSING_METRIC_FORMULA':
            case 'MISSING_METRIC_SOURCE':
            case 'MISSING_SOURCE_COLUMN':
                navigate(`/metrics/${msg.objectId}`);
                break;
            case 'MISSING_DIMENSION_CONNECTIONS':
                navigate(`/metrics/${msg.objectId}?tab=dimensions`);
                break;
            case 'NO_BUSINESS_OBJECTS':
                navigate(`/model/list`);
                break;

        }
        
    }, [props.onRunBuild])

    



    // useEffect(() => {
    //     emit('pliable:notifications', {
    //         count: allMessages.length
    //     })
    // }, [allMessages.length]);
    return <>

        {!props.nodesNeedBuilding && props.notifications.length == 0 && <>
            <div className="m-2">
                <SuccessAlert>
                    Nothing to report. Your data is up to date and has no issues
                    .
                </SuccessAlert>
            </div>

        </>}

        {(props.nodesNeedBuilding || 0) > 0 && <div onClick={onClickBuild}>
            <Message
                title={`${props.nodesNeedBuilding} nodes need to be built`}
                description=""
                time={new Date()}
                type="BUILD_NEEDED" />
        </div>}
        {props.notifications.map((m, idx) => <div onClick={() => {
            onClickMessage(m);
        }} key={idx}>
            <Message {...m}/>

        </div>)}
    </>
}

export const confirmRunBuild = async (onComplete?: () => void) => {
    const build_type = await getPromptAnswer('How do you want to refresh your data?', 'Refresh All Data', false, 'basic', [
        {
            label: 'Simple Refresh',
            value: 'basic',
            description: 'Just refresh any nodes that have changed configuration since the last run.'
        },
        {
            label: 'Check for New Data + Refresh',
            value: 'check_for_records',
            description: 'Pliable will check your data sources for new data before refreshing.'
        },
        {
            label: 'Force Refresh',
            description: 'This will refresh all nodes, even if they are up to date. Might be expensive if you have a lot of data in Snowflake.',
            value: 'force'
        }
    ]);
    if (build_type) {
        try {
            switch (build_type) {
                case 'check_for_records':
                    await runBuild('', false, true, undefined, undefined, undefined, onComplete);
                    break;
                case 'force':
                    await runBuild('', true, true, undefined, undefined, undefined, onComplete);
                    break;
                case 'basic':
                default:
                    await runBuild('', false, undefined, undefined, undefined, undefined, onComplete);
                    break;
            }
        } catch (err) {
            toast('danger', 'Error', getErrorMessage(err));
        }
    }
};

const Inbox = () => {
    const [hasNotifications, setHasNotifications] = useState(false);

    const notifications = useNotifications();
    const nodesThatNeedBuilding = useNodesThatNeedBuilding();
    const navigate = useNavigate();

    const measures = useMeasures();

    const pipelineNodes = usePipelineNodes();
    const relationships = usePipelineNodeRelationships();

    const staticAlerts = useMemo(() => {
        if (!pipelineNodes.data || !relationships.data || !measures.data) {
            console.log('Skipping static alerts');
            return [];
        }

        const boNodes = pipelineNodes.data.filter(n => ['DATE_DIMENSION', 'DIMENSION'].includes(n.node_type));
        const nodeIdsWithRelationships = relationships.data.map(r => r.parent_node_id).concat(relationships.data.map(r => r.child_node_id));
        
        const alerts: MessageProps[] = [];

        if (boNodes.length == 0) {
            alerts.push({
                title: 'No entities',
                description: 'You should create some entities to get started.',
                time: new Date(),
                type: 'NO_BUSINESS_OBJECTS'
            });
        }
        boNodes.forEach(n => {
            
            if (boNodes.length > 1 && !nodeIdsWithRelationships.includes(n.id as string)) {
                alerts.push({
                    title: `Missing relationships`,
                    description: 'You should connect it to the rest of your data model.',
                    time: n.last_updated || new Date(),
                    type: 'MISSING_RELATIONSHIPS',
                    objectId: n.id as string,
                    objectType: 'pipeline_node'
                });
            }

            if (n.upstream_node_ids.length == 0 && n.node_type == 'DIMENSION') {
                alerts.push({
                    title: 'Missing source data',
                    description: '',
                    time: n.last_updated || new Date(),
                    type: 'MISSING_MAPPINGS',
                    objectId: n.id as string,
                    objectType: 'pipeline_node',
                })
            }
        });

        console.log('About to parse metrics', measures.data.length);

        measures.data.forEach(m => {
            console.log('Validating metric', m.name);
            const metricAlerts = validateMetric(m);
            metricAlerts.forEach(alert => {
                alerts.push(alert);
            });
            
        })
        


        

        return alerts;
    }, [pipelineNodes.dataUpdatedAt, relationships.dataUpdatedAt, measures.dataUpdatedAt]);


    const allMessages = useMemo(() => {
        const messages: MessageProps[] = (notifications.data || []).map(n => {
            return {
                title: n.title,
                description: '',
                time: n.timestamp,
                type: n.type,
                objectId: n.object_id,
                objectType: n.object_type,
            };
        });
        return [...staticAlerts].concat(messages);
    }, [notifications.dataUpdatedAt, staticAlerts]);


    const processNotificationCount = (data: any) => {
        setHasNotifications(data.count > 0);
    }


    useHotkeys('shift+ctrl+a', () => {
        confirmRunBuild();
    }, []);

    
    return <>

        <DraftOnly>
            <BSDropdown>
                {(nodesThatNeedBuilding.data && nodesThatNeedBuilding.data.length > 0 || allMessages.length > 0) && <Indicator />}

                <BSDropdown.Toggle title="Notifications" className={`nocaret me-4`} variant="inverted" id="dropdown-basic">
                    <i className="mdi mdi-bell font-24"></i>

                </BSDropdown.Toggle>
                <BSDropdown.Menu style={{
                    width: '500px',
                    overflowY: 'scroll',
                    maxHeight: '90vh',
                }}>
                    <NotificationsList onRunBuild={confirmRunBuild} notifications={allMessages} nodesNeedBuilding={nodesThatNeedBuilding.data?.length}/>
                </BSDropdown.Menu>
            </BSDropdown>
        </DraftOnly>
        <ProdOnly>
            <button className="btn btn-xs btn-primary me-4" onClick={() => {
                confirmRunBuild();
            }} title="Build All (Ctrl+Shift+A)">
                <i className="mdi mdi-hammer-screwdriver" style={{ fontSize: '20px' }}></i>
            </button>
        </ProdOnly>
    </>
}

export default Inbox;
